function getFocusableElements(container) {
  if (!container) return []; // ← this line prevents the error

  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  ).filter(el => !el.closest('[inert]'));
}


class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    // if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
       // Workaround for safari iframe bug

      const formerStyle = deferredElement.getAttribute('style');

      deferredElement.setAttribute('style', 'display: block;');

      window.setTimeout(() => {

        deferredElement.setAttribute('style', formerStyle);

      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.loadRecommendations(this.dataset.productId);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}


customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}
class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

function setShortViewportHeight() {
  const value = document.documentElement.clientHeight;
  const currentValue = getComputedStyle(
    document.documentElement
  ).getPropertyValue("--100-svh");
  if (value != currentValue) {
    document.documentElement.style.setProperty("--100-svh", `${value}px`);
  }
}

function setDynamicViewportHeight() {
  const value = window.innerHeight;
  const currentValue = getComputedStyle(
    document.documentElement
  ).getPropertyValue("--100-dvh");

  if (value != currentValue) {
    document.documentElement.style.setProperty("--100-dvh", `${value}px`);
  }
}

function setHeaderHeight() {
  const header = document.querySelector(".header-main");
  if (!header) return;
  const value = header.getBoundingClientRect().height;
  const currentValue = getComputedStyle(
    document.documentElement
  ).getPropertyValue("--header-height");

  if (value && value != currentValue) {
    document.documentElement.style.setProperty("--header-height", `${value}px`);
  }
}

function setCustomPropertiesOnLoad() {
  setShortViewportHeight();
  setDynamicViewportHeight();

  setHeaderHeight();
}

function setCustomPropertiesOnScroll() {
  setShortViewportHeight();
  setDynamicViewportHeight();
}

function setCustomPropertiesOnResize() {
  setShortViewportHeight();
  setDynamicViewportHeight();

  setHeaderHeight();
}

window.addEventListener("load", setCustomPropertiesOnLoad, false);

window.addEventListener("scroll", debounce(setCustomPropertiesOnScroll, 300), false);

window.addEventListener("resize", debounce(setCustomPropertiesOnResize, 300), false);

// Klaviyo form for footer
document.addEventListener("DOMContentLoaded", () => {
  const newsletterForm = document.getElementById("newsletter-footer");
  if (!newsletterForm) return;

  const emailInput = document.getElementById("footer-newsletter-input");
  const invalidMessage = document.querySelector(".footer-newsletter-messages .invalid-email");
  const emptyMessage = document.querySelector(".footer-newsletter-messages .empty-email");
  const successMessage = document.querySelector(".footer-newsletter-messages .success-message");
  const listId = newsletterForm.dataset.klaviyoListId;
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const showMessage = (element) => {
    emptyMessage.classList.add("hidden");
    invalidMessage.classList.add("hidden");
    if (element) element.classList.remove("hidden");
  };

   const hideMessage = (element) => {
    if (element) element.classList.add("hidden");
  };

  setTimeout(() => {
    hideMessage();
  }, 5000);

  const validateEmail = () => {
    const email = emailInput.value.trim();
    if (!email) {
      showMessage(emptyMessage);
      setTimeout(() => {
        hideMessage(emptyMessage);
      }, 5000);
      return false;
    }
    if (!emailPattern.test(email)) {
      showMessage(invalidMessage);
      setTimeout(() => {
        hideMessage(invalidMessage);
      }, 5000);
      return false;
    }
    showMessage(null);
    return true;
  };

  const subscribe = async (email) => {
    try {
      if (!listId) {
        console.error("Klaviyo List ID is missing.");
        return;
      }

      const response = await fetch("https://a.klaviyo.com/client/subscriptions/?company_id=JLBJ7v", {
        method: "POST",
        headers: {
          accept: "application/vnd.api+json",
          revision: "2025-01-15",
          "content-type": "application/vnd.api+json"
        },
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: {
              custom_source: "Footer Form",
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email,
                    subscriptions: {
                      email: {
                        marketing: { consent: "SUBSCRIBED" }
                      }
                    }
                  }
                }
              }
            },
            relationships: {
              list: { data: { type: "list", id: listId } }
            }
          }
        })
      });

      if (response.status === 202) {
        emailInput.value = "";
        // emailInput.placeholder = "You're subscribed!";
        newsletterForm.setAttribute("data-submitted", "true");

        // const successMsg = document.createElement("small");
        // successMsg.className = "input-success typography--body-1";
        // successMsg.textContent = "Thank you for subscribing!";
        // newsletterForm.appendChild(successMsg);
        showMessage(successMessage);
        setTimeout(() => {
          hideMessage(successMessage);
          newsletterForm.setAttribute("data-submitted", "false");
        }, 5000);
        
      } else {
        throw new Error("Failed to subscribe");
      }
    } catch (error) {
      const span = invalidMessage.querySelector("span");
      if (span) span.textContent = "Subscription failed. Please try again.";
      showMessage(invalidMessage);
      setTimeout(() => {
          hideMessage(invalidMessage);
        }, 5000);
    }
  };

  newsletterForm.addEventListener("submit", (event) => {
    event.preventDefault();
    if (validateEmail()) {
      subscribe(emailInput.value);
    }
  });
});


class featuredCollectionSlider extends HTMLElement {
  constructor() {
      super();
  }
  connectedCallback() {
      const slidesDesktop = this.dataset.slidesDesktop;
      const slidesMobile = this.dataset.slidesMobile;
      const enabledMobile = this.dataset.enabledMobile === "true";
      const enabledDesktop = this.dataset.enabledDesktop === "true";
    
      this.swiper = new Swiper(this, {
        enabled: enabledMobile,
        slidesPerView: slidesMobile,
        spaceBetween: 6,
        mousewheel: {
          forceToAxis: true
        },
        freeMode: true,
        breakpoints: {
            769: {
                slidesPerView: slidesDesktop,
                enabled: enabledDesktop,
                spaceBetween: 8
            }
        },
        scrollbar: {
          el: ".featured-collection-scrollbar",
          draggable: true
        }
      });
  }
}
customElements.define("featured-collection-slider", featuredCollectionSlider);

class hpBannerFW extends HTMLElement {
  constructor() {
    super();
    this.handleMediaToggle = this.handleMediaToggle.bind(this);
    this.handleAutoPlay = this.handleAutoPlay.bind(this);
  }

  connectedCallback() {
    this.addEventListener("click", this.handleClick);
    this.addEventListener("keydown", this.handleKeyDown);
    this.handleAutoPlay();
  }

  handleClick(event) {
    if (event.target.classList.contains("hp-banner-fw-media-toggle")) {
      this.handleMediaToggle(event);
    } else if (event.target.closest(".hp-banner-fw-media-volume")) {
    }
  }

  handleKeyDown(event) {
    if (event.key === "Enter") {
      this.handleClick(event);
    }
  }

  handleMediaToggle(event) {
    let mediaContainer = this.querySelector(".hp-banner-fw-media");
    if (!mediaContainer) return;

    let videos = mediaContainer.querySelectorAll("video");
    let video = videos.length > 1 && window.matchMedia("(max-width: 768px)").matches ? videos[1] : videos[0];

    if (!video) return;

    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  }

  handleAutoPlay() {
    let mediaContainer = this.querySelector(".hp-banner-fw-media");
    if (!mediaContainer) return;

    let videos = mediaContainer.querySelectorAll("video");
    let video = videos.length > 1 && window.matchMedia("(max-width: 768px)").matches ? videos[1] : videos[0];

    if (!video) return;

    if(mediaContainer.dataset.autoplay === "true") {
      video.play();
    }
  }

}

customElements.define("hp-banner-fw", hpBannerFW);

// class ProductCardVariantSelects extends HTMLElement {
//   constructor() {
//     super();

//     this.variants = [];
//     const variantData = this?.dataset?.productVariants;
//     if (variantData) {
//       try {
//         this.variants = JSON.parse(variantData);
//       } catch (err) {
//         console.warn('Failed to parse variant JSON', err);
//       }
//     }

//     this.onChange = this.onChange.bind(this);
//     this.handleKeyDown = this.handleKeyDown.bind(this);
//   }

//   connectedCallback() {
//     // Handle size change → add to cart
//     this.addEventListener('change', this.onChange);

//     // Add keyboard navigation to radios
//     const fieldset = this.querySelector('fieldset');
//     if (fieldset) {
//       fieldset.addEventListener('keydown', this.handleKeyDown);
//     }
//   }

//   async onChange(event) {
//     const input = event.target;
//     if (!(input.tagName === 'INPUT' && input.type === 'radio')) return;
//     if (!/size/i.test(input.name)) return;

//     const checkedInputs = this.querySelectorAll('input[type="radio"]:checked');
//     const selectedOptions = Array.from(checkedInputs).map(i => i.value);

//     const matchingVariant = this.variants.find(v =>
//       JSON.stringify(v.options) === JSON.stringify(selectedOptions)
//     );

//     if (!matchingVariant) {
//       console.warn('No matching variant found for', selectedOptions);
//       return;
//     }

//     try {
//       const addResp = await fetch('/cart/add.js', {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({ id: matchingVariant.id, quantity: 1 }),
//       });

//       if (!addResp.ok) throw new Error('Add to cart failed');
//       await addResp.json();

//       const sectionsResp = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`);
//       const sections = await sectionsResp.json();

//       const cartDrawer = document.querySelector('cart-drawer');
//       const body = document.querySelector('body');
//       if (cartDrawer) {
//         cartDrawer.classList.remove('is-empty');
//         body.classList.add('overflow-hidden');
//         cartDrawer.renderContents({
//           id: matchingVariant.id,
//           sections,
//         });
//       }
//     } catch (error) {
//       console.error('Cart update failed:', error);
//     }
//   }

//   handleKeyDown(event) {
//   const fieldset = event.currentTarget;
//   const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]:not(.disabled)'));
//   const active = document.activeElement;
//   let currentRadio = null;

//   // Figure out which radio is "current"
//   if (active && active.tagName === 'LABEL') {
//     const forId = active.getAttribute('for');
//     if (forId) currentRadio = document.getElementById(forId);
//   } else if (active && active.tagName === 'INPUT' && active.type === 'radio') {
//     currentRadio = active;
//   }

//   const currentIndex = currentRadio ? radios.indexOf(currentRadio) : -1;

//   // Arrow navigation
//   if ((event.key === 'ArrowDown' || event.key === 'ArrowRight') && currentIndex !== -1) {
//     event.preventDefault();
//     const nextIndex = (currentIndex + 1) % radios.length;
//     const nextLabel = fieldset.querySelector(`label[for="${CSS.escape(radios[nextIndex].id)}"]`);
//     if (nextLabel) {
//       nextLabel.setAttribute('tabindex', '0');
//       nextLabel.focus();
//     }
//     return;
//   }

//   if ((event.key === 'ArrowUp' || event.key === 'ArrowLeft') && currentIndex !== -1) {
//     event.preventDefault();
//     const prevIndex = (currentIndex - 1 + radios.length) % radios.length;
//     const prevLabel = fieldset.querySelector(`label[for="${CSS.escape(radios[prevIndex].id)}"]`);
//     if (prevLabel) {
//       prevLabel.setAttribute('tabindex', '0');
//       prevLabel.focus();
//     }
//     return;
//   }

//   // Enter / Space should select the focused radio
//   if (event.key === 'Enter' || event.key === ' ') {
//     event.preventDefault();
//     if (active && active.tagName === 'LABEL') {
//       const forId = active.getAttribute('for');
//       const input = document.getElementById(forId);
//       if (input && !input.classList.contains('disabled')) {
//         input.checked = true;
//         input.dispatchEvent(new Event('change', { bubbles: true }));
//       }
//     } else if (active && active.tagName === 'INPUT' && active.type === 'radio') {
//       if (!active.classList.contains('disabled')) {
//         active.checked = true;
//         active.dispatchEvent(new Event('change', { bubbles: true }));
//       }
//     }
//   }
// }

// }

// customElements.define('product-card-variant-selects', ProductCardVariantSelects);

class ProductCardVariantSelects extends HTMLElement {
  constructor() {
    super();

    this.variants = [];
    const variantData = this?.dataset?.productVariants;
    if (variantData) {
      try {
        this.variants = JSON.parse(variantData);
      } catch (err) {
        console.warn('Failed to parse variant JSON', err);
      }
    }

    this.onButtonClick = this.onButtonClick.bind(this);
  }

  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('button[type="button"]'));
    this.buttons.forEach(btn => {
      btn.addEventListener('click', this.onButtonClick);
    });
  }

  async onButtonClick(event) {
    const clickedButton = event.currentTarget;

    // Remove active class from all in same fieldset, add to clicked
    const fieldset = clickedButton.closest('fieldset');
    if (fieldset) {
      fieldset.querySelectorAll('button.active').forEach(btn => btn.classList.remove('active'));
      clickedButton.classList.add('active');
    }

    // Get selected options from all active buttons
    const selectedOptions = Array.from(this.querySelectorAll('fieldset'))
      .map(fs => {
        const activeBtn = fs.querySelector('button.active');
        return activeBtn ? activeBtn.value : null;
      })
      .filter(v => v !== null);

    // Only proceed if all options are selected
    if (selectedOptions.length === 0) return;

    const matchingVariant = this.variants.find(v =>
      JSON.stringify(v.options) === JSON.stringify(selectedOptions)
    );

    if (!matchingVariant) {
      console.warn('No matching variant found for', selectedOptions);
      return;
    }
    // Collect line item properties
    const propertyInputs = this.querySelectorAll('[name^="properties"]');
    const properties = {};

    propertyInputs.forEach(input => {
      if (input.value) {
        // Extract the key inside brackets: properties[Engraving] → Engraving
        const match = input.name.match(/^properties\[(.+)\]$/);
        if (match && match[1]) {
          properties[match[1]] = input.value;
        }
      }
    });


    try {
      const addResp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: matchingVariant.id, quantity: 1 ,properties: properties}),
      });

      if (!addResp.ok) throw new Error('Add to cart failed');
      await addResp.json();

      const sectionsResp = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`);
      const sections = await sectionsResp.json();

      const cartDrawer = document.querySelector('cart-drawer');
      const body = document.querySelector('body');
      if (cartDrawer) {
        cartDrawer.classList.remove('is-empty');
        body.classList.add('overflow-hidden');
        cartDrawer.renderContents({
          id: matchingVariant.id,
          sections,
        });
      }
    } catch (error) {
      console.error('Cart update failed:', error);
    }
  }
}

customElements.define('product-card-variant-selects', ProductCardVariantSelects);


class ProductCardComponent extends HTMLElement {
  constructor() {
    super();

    this.swatches = this.querySelectorAll('.swatch');
    this.datasetTemplate = this.dataset.template;

    if (!this.swatches.length) {
      console.warn('No .swatch buttons found');
      return;
    }

    this.initSwatchListeners();
  }

  initSwatchListeners() {
    this.swatches.forEach(button => {
      button.addEventListener('click', () => {
        const productUrl = button.dataset.productUrl;

        if (!productUrl) {
          console.error('Swatch missing data-product-url');
          return;
        }

        // const sectionId = 'product-card-section'; // Update if your section has a different ID
        // const sectionId_clp = 'product-card-clp-section'; 
        // const fetchUrl = `${window.Shopify.routes.root}products/${productUrl}?section_id=${sectionId}`;

        const isCollectionPage = this.datasetTemplate === "collection";

        const sectionId = isCollectionPage 
          ? 'product-card-clp-section' 
          : 'product-card-section';

        const fetchUrl = `${window.Shopify.routes.root}products/${productUrl}?section_id=${sectionId}`;


        console.log('Fetching:', fetchUrl);

        fetch(fetchUrl)
          .then(response => {
            if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
            return response.text();
          })
          .then(html => {
            const doc = new DOMParser().parseFromString(html, 'text/html');

            // Extract new content
            const newImage = doc.querySelector('.product-card-media');
            const newMedia = doc.querySelector('.product-card-media-mobile');
            const newTitle = doc.querySelector('.product-card-title');
            const newPrice = doc.querySelector('.product-card-price-wrapper');
            const variantPicker = doc.querySelector('.product-card-variant-picker');

            if (newImage && this.querySelector('.product-card-media')) {
              this.querySelector('.product-card-media').innerHTML = newImage.innerHTML;
              // console.log('Updated image');
            }
            if (newMedia && this.querySelector('.product-card-media-mobile')) {
              this.querySelector('.product-card-media-mobile').innerHTML = newMedia.innerHTML;
              console.log('Updated product-card-media-mobile');
            }
            if (newTitle && this.querySelector('.product-card-title')) {
              this.querySelector('.product-card-title').innerHTML = newTitle.innerHTML;
              // console.log('Updated title');
            }

            if (newPrice && this.querySelector('.product-card-price-wrapper')) {
              this.querySelector('.product-card-price-wrapper').innerHTML = newPrice.innerHTML;
              // console.log('Updated price');
            }

            if (variantPicker) {
              this.querySelector('.product-card-variant-picker').innerHTML = variantPicker.innerHTML;
              // console.log('Updated variants');
            }

            // Update swatch active state
            this.querySelectorAll('.swatch').forEach(s => s.classList.remove('active_swatch'));
            button.classList.add('active_swatch');
          })
          .catch(err => console.error('Swatch load error:', err));
      });
    });
  }
}

customElements.define('product-card-component', ProductCardComponent);

class pdpMediaSlider extends HTMLElement {
  constructor() {
      super();
  }
  connectedCallback() {    
      this.swiper = new Swiper(this, {
        slidesPerView: 1,
        spaceBetween: 0,
        mousewheel: {
            forceToAxis: true
        },
        pagination: {
        el: ".swiper-pagination",
        clickable: true,
        type: "bullets", // optional, default is bullets
      }
      });
  }
}
customElements.define("pdp-media-slider", pdpMediaSlider); 

class prodRecommendationSlider extends HTMLElement {
  constructor() {
      super();
  }
   connectedCallback() {
      const slidesDesktop = this.dataset.slidesDesktop;
      const slidesMobile = this.dataset.slidesMobile;
    
      this.swiper = new Swiper(this, {
        slidesPerView: slidesMobile,
        spaceBetween: 6,
        freeMode: true,
        mousewheel: {
            forceToAxis: true
        },
        breakpoints: {
            769: {
                slidesPerView: slidesDesktop,

            }
        },
        scrollbar: {
          el: ".swiper-scrollbar",
          draggable: true
        },
      });
  }
}
customElements.define("product-recommendation-slider", prodRecommendationSlider); 

class prodCardsMediaSlider extends HTMLElement {
  constructor() {
      super();
  }
   connectedCallback() {
    
      this.swiper = new Swiper(this, {
        slidesPerView: 1,
        mousewheel: {
            forceToAxis: true
        },
        scrollbar: {
          el: ".swiper-scrollbar",
          draggable: true
        },
      });
  }
}
customElements.define("product-card-media-slider", prodCardsMediaSlider);

class ColorCollection extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
  }

  connectedCallback() {
    this.initializeSwiper();
  }

  initializeSwiper() {
    const config = this.getSwiperConfig();
    this.swiper = new Swiper(this, config);
    this.classList.remove("color-collection-carousel--loading");
  }

  getSwiperConfig() {
    const slidesDesktop = parseInt(this.dataset.slidesDesktop);
    const slidesMobile = parseInt(this.dataset.slidesMobile);
    const scrollBarDesktop = this.dataset.scrollbarDesktop === "true";
    const scrollBarMobile = this.dataset.scrollbarMobile === "true";
    const scrollbarElement = this.querySelector(".swiper-scrollbar");
    const arrowsWrapper = this.closest(".color-collection-cards-wrapper").querySelector(".clp-arrows-wrapper");
    const arrowNext = arrowsWrapper.querySelector(".swiper-button-next");
    const arrowPrev = arrowsWrapper.querySelector(".swiper-button-prev");
    const arrowsDesktop = this.dataset.arrowDesktop === "true";
    const arrowsMobile = this.dataset.arrowMobile === "true";

    return {
      slidesPerView: slidesMobile,
      spaceBetween: 8,
      freeMode: false,
      mousewheel: { forceToAxis: true },
      navigation: {
        nextEl: arrowsMobile ? arrowNext : null,
        prevEl: arrowsMobile ? arrowPrev : null,
      },
      scrollbar: {
        el: scrollBarMobile ? scrollbarElement : null,
        enable: false,
        hide: false,
        draggable: true,
      },
      breakpoints: {
        1140: {
          slidesPerView: slidesDesktop,
          navigation: {
            nextEl: arrowsDesktop ? arrowNext : null,
            prevEl: arrowsDesktop ? arrowPrev : null,
          },
          scrollbar: {
            el: scrollBarDesktop ? scrollbarElement : null,
            enable: false,
          },
        }

      },
    };
  }
}

customElements.define("color-collection-carousel", ColorCollection);

class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();
    this.maxItems = 4;
    this.sectionId = 'product-card-clp-section';
    this.storageKey = 'recently_viewed';
  }

  connectedCallback() {
  if (Shopify && Shopify.designMode) return;

    this.contentBlock = this.querySelector('.js-recentPdpBlock');
    this.sectionWrapper = document.querySelector('.recent-products-section');

    if (!this.contentBlock) return;

    this.renderIfReady();

    // Re-render when product is added to localStorage
    window.addEventListener('recentlyViewedUpdated', () => {
      this.renderIfReady();
    });
  }
  renderIfReady() {
    this.toggleSectionVisibility();
    this.renderRecentlyViewed();
  }

  toggleSectionVisibility() {
    const pdpData = JSON.parse(localStorage.getItem(this.storageKey));
    const hasProducts = Array.isArray(pdpData) && pdpData.length > 0;

    if (this.sectionWrapper) {
      this.sectionWrapper.classList.toggle('hidden', !hasProducts);
    }
  }

  async renderRecentlyViewed() {
    const pdpData = JSON.parse(localStorage.getItem(this.storageKey));
    if (!Array.isArray(pdpData) || !pdpData.length) return;

    this.contentBlock.innerHTML = '';

    for (const item of pdpData) {
      try {
        const url = new URL(item.productUrl, window.location.origin);
        const handle = url.pathname.replace(/^\/products\//, '').replace(/\/$/, '');
        const fetchUrl = `${window.Shopify.routes.root}products/${handle}?section_id=${this.sectionId}`;

        const response = await fetch(fetchUrl);
        if (!response.ok) throw new Error(`Failed to fetch ${handle}`);

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const sectionHTML = doc.querySelector(`#shopify-section-${this.sectionId}`)?.innerHTML;

        if (sectionHTML) {
          const wrapper = document.createElement('div');
          wrapper.innerHTML = sectionHTML;
          this.contentBlock.appendChild(wrapper);
        }
      } catch (error) {
        console.error('Error loading recently viewed product:', error);
      }
    }
  }
}

customElements.define("recently-viewed-products", RecentlyViewedProducts);

class PdpZoomModal extends HTMLElement {
  constructor() {
    super();
    this.imageContainer = this.querySelector('.zoom_media_container');
    this.zoomedImage = this.querySelector('.zoomed_image');
    this.closeButton = this.querySelector('.pdp_zoom_modal_close');
    this.nextButton = this.querySelector('.zoom-button-next');
    this.prevButton = this.querySelector('.zoom-button-prev');

    this.openers = [];
    this.imageSrcList = [];
    this.currentIndex = 0;

    this.handleKeyDown = this.handleKeyDown.bind(this); // bind for focus trap
  }

  connectedCallback() {
    this.initOpeners();
    this.initClose();
    this.initNavigation();
  }

  initOpeners() {
    this.openers = Array.from(document.querySelectorAll('.zoom-modal-opener'));
    this.imageSrcList = this.openers.map(opener => opener.dataset.image_src);

    this.openers.forEach((opener, index) => {
      opener.addEventListener('click', (e) => {
        e.preventDefault();
        this.currentIndex = index;
        this.showImage(this.currentIndex);
        this.openModal();
      });
    });
  }

  initClose() {
    this.closeButton?.addEventListener('click', () => {
      this.closeModal();
    });
  }

  initNavigation() {
    this.nextButton?.addEventListener('click', () => {
      this.currentIndex = (this.currentIndex + 1) % this.imageSrcList.length;
      this.showImage(this.currentIndex);
    });

    this.prevButton?.addEventListener('click', () => {
      this.currentIndex = (this.currentIndex - 1 + this.imageSrcList.length) % this.imageSrcList.length;
      this.showImage(this.currentIndex);
    });
  }

  openModal() {
    this.classList.remove('hidden');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');

    // Focus modal and trap focus inside
    this.focus({ preventScroll: true });
    document.addEventListener('keydown', this.handleKeyDown);
  }

  closeModal() {
    this.classList.add('hidden');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');

    // Remove focus trap
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  showImage(index) {
    const src = this.imageSrcList[index];
    if (this.zoomedImage && src) {
      this.zoomedImage.src = src;
    }
  }

  handleKeyDown(e) {
    if (e.key === 'Tab') {
      const focusableEls = this.getFocusableElements();
      if (!focusableEls.length) return;

      const firstEl = focusableEls[0];
      const lastEl = focusableEls[focusableEls.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }

    // Optional: close modal on Escape
    if (e.key === 'Escape') {
      this.closeModal();
    }
  }

  getFocusableElements() {
    return Array.from(
      this.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(el => !el.disabled && !el.hasAttribute('hidden'));
  }
}

customElements.define('pdp-zoom-modal', PdpZoomModal);





class featuresCardSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.swiper = new Swiper(this, {
      slidesPerView: 2.1,
      spaceBetween: 6,
      centeredSlides: true,
      loop: true,
      mousewheel: {
        forceToAxis: true,
      },
      freeMode: true,
      breakpoints: {
        768: {
          slidesPerView: 4,
          spaceBetween: 8,
          centeredSlides: false,
          loop: false,
        },
      },
    });
  }

}
customElements.define('features-card-slider', featuresCardSlider);

class MediaControls extends HTMLElement {
  constructor() {
    super();
    this.handleMediaToggle = this.handleMediaToggle.bind(this);
    this.handleMediaVolume = this.handleMediaVolume.bind(this);
    this.handleVideoEnd = this.handleVideoEnd.bind(this);
    this.handleAutoPlay = this.handleAutoPlay.bind(this);
  }

  connectedCallback() {
    this.addEventListener("click", this.handleClick);
    // this.addEventListener("keydown", this.handleKeyDown);
    this.closest('.video-wrapper').querySelectorAll("video").forEach((video) => {
      if (!video.hasAttribute("loop")) {
        video.addEventListener("ended", this.handleVideoEnd);
      }
    });
    this.handleAutoPlay();
  }

  handleClick(event) {
    const button = event.target.closest("button");

    if (!button) return;

    if (button.classList.contains("media-toggle")) {
      this.handleMediaToggle(button);
    } else if (button.classList.contains("volume-toggle")) {
      this.handleMediaVolume(button);
    }
  }

  handleKeyDown(event) {
    if (event.key === "Enter") {
      this.handleClick(event);
    }
  }

  handleMediaToggle(button) {
    let mediaContainer = this.closest(".video-wrapper");
    if (!mediaContainer) return;

    let videos = mediaContainer.querySelectorAll("video");
    let video = videos.length > 1 && window.matchMedia("(max-width: 768px)").matches ? videos[1] : videos[0];

    if (!video) return;

    if (video.paused) {
      video.play();
      button.classList.remove('paused');
    } else {
      video.pause();
      button.classList.add('paused');
    }
  }

  handleMediaVolume(button) {
    let mediaContainer = this.closest(".video-wrapper");
    if (!mediaContainer) return;

    let videos = mediaContainer.querySelectorAll("video");
    let video = videos.length > 1 && window.matchMedia("(max-width: 768px)").matches ? videos[1] : videos[0];

    if (!video) return;

    video.muted = !video.muted;

    button.classList.toggle("unmuted", !video.muted);
  }

  handleVideoEnd() {
    let toggleButton = this.querySelector(".media-toggle");
    if (toggleButton) {
      toggleButton.classList.add('paused');
    }
  }

  handleAutoPlay() {
    let mediaContainer = this.querySelector(".video-wrapper");
    if (!mediaContainer) return;

    let videos = mediaContainer.querySelectorAll("video");
    let video = videos.length > 1 && window.matchMedia("(max-width: 768px)").matches ? videos[1] : videos[0];

    if (!video) return;

    if(mediaContainer.dataset.autoplay === "true") {
      video.play();
    }
  }

}

customElements.define("video-controls", MediaControls);

class announcementSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.swiper = new Swiper(this, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: true,
      autoplay: {
        delay: 6000,
        disableOnInteraction: false,
      },
      mousewheel: {
        forceToAxis: true,
      },
      freeMode: true,
    });
  }

}
customElements.define('announcement-bar', announcementSlider);

class cartRecommendationSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.swiper = new Swiper(this, {
      slidesPerView: 1,
      grabCursor: true,
      mousewheel: {
            forceToAxis: true
        },
       navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      }
    });
  }

}
customElements.define('cart-drawer-recommendations', cartRecommendationSlider);

class cartVariantDropdown extends HTMLElement {
  constructor() {
    super();
    this.fieldset = this.querySelector('fieldset');
    this.closeButton = this.querySelector('.size_close_button');
    this.closeButton.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault(); // Prevents page scrolling on Space
        this.click();
      }
    });
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
  }

  connectedCallback() {
    if (!this.fieldset) return;

    // Handle external trigger button (matches fieldset ID)
    const externalButton = document.getElementById(this.fieldset.id);
    if (externalButton) {
      externalButton.addEventListener('click', (event) => {
        event.stopPropagation();
        if (!this.fieldset.classList.contains('is-open')) {
          this.openDropdown();
        }
      });
    }

    // Close on radio change
    const radios = this.fieldset.querySelectorAll('input[type="radio"]');
    radios.forEach((radio) => {
      radio.addEventListener('change', () => this.closeDropdown());
    });

    // Close button inside dropdown
    if (this.closeButton) {
      this.closeButton.addEventListener('click', (event) => {
        event.stopPropagation();
        this.closeDropdown();
      });
    }
  }

  initFocusTrap() {
    const container = this.fieldset;
    if (!container) return;

    const selector = `
      button:not([disabled]),
      input[type="radio"]:not([disabled]),
      input:not([type="hidden"]):not([disabled]),
      select:not([disabled]),
      textarea:not([disabled]),
      [tabindex]:not([tabindex="-1"])
    `;

    const getFocusable = () =>
      [...container.querySelectorAll(selector)].filter(el => el.offsetParent !== null);

    this.handleKeydown = (e) => {
      if (e.key !== 'Tab') return;

      const focusable = getFocusable();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!container.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
        return;
      }

      // SHIFT + TAB
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }

      // TAB
      else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', this.handleKeydown);

    const focusable = getFocusable();
    focusable[0]?.focus();
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.closeDropdown();
    }
  }

  toggleDropdown() {
    const isOpen = this.fieldset.classList.toggle('is-open');
    document.body.classList.toggle('variants_overlay', isOpen);
    document.body.classList.toggle('overflow-hidden', isOpen);

    if (isOpen) {
      this.fieldset.setAttribute('tabindex', '-1');
      document.addEventListener('click', this.handleDocumentClick);
    } else {
      document.removeEventListener('click', this.handleDocumentClick);
    }
  }

  openDropdown() {
    this.fieldset.classList.add('is-open');
    document.body.classList.add('variants_overlay');
    document.body.classList.add('overflow-hidden');
    this.fieldset.setAttribute('tabindex', '-1');
    document.addEventListener('click', this.handleDocumentClick);
    this.initFocusTrap();
  }

  closeDropdown() {
    this.fieldset.classList.remove('is-open');
    document.body.classList.remove('variants_overlay');
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('click', this.handleDocumentClick);
    if (this.handleKeydown) {
      this.fieldset.removeEventListener('keydown', this.handleKeydown);
    }
  }
}

customElements.define('cart-variant-dropdown', cartVariantDropdown);





class CartRecommendationsVariantPicker extends HTMLElement {
  constructor() {
    super();

    this.variants = [];
    const variantData = this.closest('.cart-recommendations-variant-picker')?.dataset?.productVariants;
    if (variantData) {
      try {
        this.variants = JSON.parse(variantData);
      } catch (err) {
        console.warn('Failed to parse variant JSON', err);
      }
    }

    // bind handlers
    this.onChange = this.onChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  connectedCallback() {
    // listen for change (mouse clicks and explicit keyboard selection will trigger this)
    this.addEventListener('change', this.onChange);

    // keyboard navigation: attach to the fieldset (so it handles label/input focus)
    const fieldset = this.querySelector('fieldset');
    if (fieldset) {
      fieldset.addEventListener('keydown', this.handleKeyDown);
    }
  }

  // safest CSS selector escape (use native if available, simple fallback otherwise)
  escapeSelector(s) {
    if (window.CSS && CSS.escape) return CSS.escape(s);
    // basic fallback escaping for most special characters
    return s.replace(/([ !"#$%&'()*+,.\/:;<=>?@[\\\]^`{|}~])/g, '\\$1');
  }

  pushGA4RecommendationEvent(matchingVariant) {
    if (!matchingVariant) return;
    const el = this.closest('[data-reco-product]');
    if (!el) return;
    const productId = el.getAttribute('data-product-id');
    const productName = el.getAttribute('data-product-name');
    const productVendor = el.getAttribute('data-product-vendor');
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'mini_cart_recommendation_add',
      ecommerce: {
        currencyCode: Shopify.currency.active || 'USD',
        add: {
          actionField: { list: 'mini_cart_recommendations' },
          products: [{
            id: matchingVariant.sku || '',                  
            name: productName || '',              
            brand: productVendor || '',            
            category: '',   
            variant: matchingVariant.title || '', 
            price: matchingVariant.price ? (matchingVariant.price / 100).toFixed(2) : '0.00', 
            quantity: 1,
            product_id: productId || '',
            variant_id: matchingVariant.id || '',
            list: 'mini_cart_recommendations',
          }]
        }
      }
    });

  }


  handleKeyDown(event) {
    const fieldset = event.currentTarget;
    const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]:not(.disabled)'));
    if (!radios.length) return;

    const active = document.activeElement;
    let currentRadio = null;

    // Normalize current radio even if a LABEL is focused
    if (active && active.tagName === 'LABEL') {
      const forId = active.getAttribute('for');
      if (forId) currentRadio = document.getElementById(forId);
    } else if (active && active.tagName === 'INPUT' && active.type === 'radio') {
      currentRadio = active;
    }

    const currentIndex = currentRadio ? radios.indexOf(currentRadio) : -1;

    // Arrow navigation: move focus to the label of next/prev radio BUT DO NOT check it
    if ((event.key === 'ArrowDown' || event.key === 'ArrowRight') && currentIndex !== -1) {
      event.preventDefault();
      const nextIndex = (currentIndex + 1) % radios.length;

      // remove previous temporary tabindex
      fieldset.querySelectorAll('label[tabindex="0"]').forEach(l => l.removeAttribute('tabindex'));

      const nextLabel = fieldset.querySelector(`label[for="${this.escapeSelector(radios[nextIndex].id)}"]`);
      if (nextLabel) {
        nextLabel.setAttribute('tabindex', '0');
        nextLabel.focus();
      }
      return;
    }

    if ((event.key === 'ArrowUp' || event.key === 'ArrowLeft') && currentIndex !== -1) {
      event.preventDefault();
      const prevIndex = (currentIndex - 1 + radios.length) % radios.length;

      // remove previous temporary tabindex
      fieldset.querySelectorAll('label[tabindex="0"]').forEach(l => l.removeAttribute('tabindex'));

      const prevLabel = fieldset.querySelector(`label[for="${this.escapeSelector(radios[prevIndex].id)}"]`);
      if (prevLabel) {
        prevLabel.setAttribute('tabindex', '0');
        prevLabel.focus();
      }
      return;
    }

    // Enter / Space -> explicitly select the focused radio (set checked + fire change)
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (active && active.tagName === 'LABEL') {
        const forId = active.getAttribute('for');
        const input = document.getElementById(forId);
        if (input && !input.classList.contains('disabled')) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else if (active && active.tagName === 'INPUT' && active.type === 'radio') {
        if (!active.classList.contains('disabled')) {
          active.checked = true;
          active.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  }

  async onChange(event) {
    const input = event.target;

    // Only proceed if it's a radio input for Size option
    if (!(input.tagName === 'INPUT' && input.type === 'radio')) return;
    // if (!/size/i.test(input.name)) return;

    // Get all selected option values from checked radio inputs
    const checkedInputs = this.querySelectorAll('input[type="radio"]:checked');
    const selectedOptions = Array.from(checkedInputs).map(i => i.value);

    const matchingVariant = this.variants.find(
      v => JSON.stringify(v.options) === JSON.stringify(selectedOptions)
    );

    if (!matchingVariant) {
      console.warn('No matching variant found for', selectedOptions);
      return;
    }

    try {
      // Add to cart
      const addResp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: matchingVariant.id, quantity: 1 }),
      });

      if (!addResp.ok) throw new Error('Add to cart failed');
      await addResp.json();


      // Reload cart sections (cart-drawer and bubble)
      const sectionsResp = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`);
      const sections = await sectionsResp.json();

      const cartDrawer = document.querySelector('cart-drawer');
      const body = document.querySelector('body');
      if (cartDrawer) {
        cartDrawer.classList.remove('is-empty');
        body.classList.add('overflow-hidden');
        cartDrawer.renderContents({
          id: matchingVariant.id,
          sections,
        });
      }
      
      
      // Push GA4 event after successful add
      // const product = await addResp.json();
      this.pushGA4RecommendationEvent(matchingVariant);
    } catch (error) {
      console.error('Cart update failed:', error);
    }
  }
}

// define (use the tag your app expects — adjust if needed)
customElements.define('cart-variant-selects', CartRecommendationsVariantPicker);

class NewsletterPopup extends HTMLElement {
  constructor() {
      super();
      this.newsletterClosedKey = "newsletterPopupClosed";
      this.newsletterExpiryKey = "newsletterPopupExpiry";
      this.newsletterExpiryTime = 7 * 24 * 60 * 60 * 1000; // 7 days
      this.sectionId = this.dataset.sectionId;
  }

  connectedCallback() {
      this.newsletterInput = this.querySelector("#popup-newsletter-input");
      this.closeBtn = this.querySelector(".newsletter-close");
      this.newsletterForm = this.querySelector("form");
      this.newsletterInvalidMessage = this.querySelector(".invalid-email");
      this.newsletterEmptyMessage = this.querySelector(".empty-email");
      this.newsletterSuccessMessage = document.querySelector(".success-message");
      this.newsletterKlaviyoListID = this.newsletterForm.dataset.klaviyoListId || "";
      // this.newslettersuccessMessage = this.newsletterForm.dataset.successMessage || "";
      this.newsletterEmailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      this.openDelay = this.dataset.openDelay || 20000;
      this.closeDelay = this.dataset.closeDelay || 10000;
      
      if(!Shopify.designMode){this.open();}
      this.closeBtn.addEventListener("click", () => this.close());
      if (this.newsletterForm) {
          this.newsletterForm.addEventListener("submit", (e) => this.handleSubmit(e));
      }
      if(Shopify.designMode){
        this.shopifyListeners();
      }
      // 🔑 Close when clicking outside
      document.addEventListener("click", (e) => {
        if (this.classList.contains("open") && !this.contains(e.target)) {
          this.close();
        }
      });
  }

  shopifyListeners() {
    document.addEventListener('shopify:section:select', (event) => {
        if (event.detail.sectionId === this.sectionId) {
            console.log("Newsletter Popup Selected");
            this.openInEditor();
        }else{
          this.closeInEditor();
        }
    });
    document.addEventListener('shopify:section:load', (event) => {
        if (event.detail.sectionId === this.sectionId) {
            console.log("Newsletter Popup Updated");
            this.openInEditor();
        }else{
          this.closeInEditor();
        }
    });
  }

  open() {
      const storedExpiry = localStorage.getItem(this.newsletterExpiryKey);
      const now = Date.now();
      
      if (!storedExpiry || now > storedExpiry) {
        setTimeout(() => {
              const cartDrawer = document.querySelector("cart-drawer");
              const sidebar = document.querySelector("#sidebar");
              const isSidebarOpen = sidebar && sidebar.classList.contains("open");
   
              if(isSidebarOpen){
                closeSidebar();
              }
              cartDrawer.close();

              this.classList.add("open");
              this.removeAttribute("inert");
              // document.body.classList.add("overflow-hidden");
              // document.body.classList.add("newsletter-overlay");
              // setTimeout(() => {
              //     trapFocus(this,this.newsletterInput );
              // }, 110);
          }, this.openDelay);
      }
  }

  close() {
      this.classList.remove("open");
      localStorage.setItem(this.newsletterClosedKey, "true");
      localStorage.setItem(this.newsletterExpiryKey, Date.now() + this.newsletterExpiryTime);
      this.setAttribute("inert", "");
      // document.body.classList.remove("overflow-hidden");
      // document.body.classList.remove("newsletter-overlay");
  }

  showNewsletterMessage(messageElement) {
      this.newsletterEmptyMessage.classList.add("hidden");
      this.newsletterInvalidMessage.classList.add("hidden");
      if (messageElement) messageElement.classList.remove("hidden");
      setTimeout(() => {
        this.newsletterEmptyMessage.classList.add("hidden");
        this.newsletterInvalidMessage.classList.add("hidden");
      }, this.closeDelay);
  }
  showSuccessMessage(messageElement) {
      this.newsletterEmptyMessage.classList.add("hidden");
      this.newsletterInvalidMessage.classList.add("hidden");
      this.newsletterSuccessMessage.classList.add("hidden");
      if (messageElement) messageElement.classList.remove("hidden");
  }

  validateNewsletterEmail() {
      const email = this.newsletterInput.value.trim();
      if (!email) return this.showNewsletterMessage(this.newsletterEmptyMessage), false;
      if (!this.newsletterEmailPattern.test(email)) return this.showNewsletterMessage(this.newsletterInvalidMessage), false;
      return this.showNewsletterMessage(null), true;
  }

  async subscribeToNewsletter(email) {
      try {
          const response = await fetch("https://a.klaviyo.com/client/subscriptions/?company_id=JLBJ7v", {
              method: "POST",
              headers: {
                  accept: "application/vnd.api+json",
                  revision: "2025-01-15",
                  "content-type": "application/vnd.api+json"
              },
              body: JSON.stringify({
                  data: {
                      type: "subscription",
                      attributes: {
                          custom_source: "Newsletter Popup",
                          profile: {
                              data: {
                                  type: "profile",
                                  attributes: {
                                      email,
                                      subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } }
                                  }
                              }
                          }
                      },
                      relationships: { list: { data: { type: "list", id: this.newsletterKlaviyoListID } } }
                  }
              })
          });
          if (response.status === 202) {
              // this.newsletterInput.placeholder = "You're subscribed!";
              this.newsletterInput.value = "";
              this.newsletterForm.setAttribute("inert", "");
              this.newsletterForm.setAttribute("data-submitted", "true");
              this.classList.add("popup-newsletter-submitted");
              // const successMsg = document.createElement("h3");
              // successMsg.className = "input-success typography--heading-1";
              // successMsg.textContent = this.newslettersuccessMessage;
              // this.newsletterForm.nextElementSibling.appendChild(successMsg);
              // this.closeBtns[1].focus();
              this.showSuccessMessage(this.newsletterSuccessMessage)
              setTimeout(() => {
                this.close();
              }, this.closeDelay);
          } else {
              throw new Error("Failed to subscribe");
          }
      } catch (error) {
          this.newsletterInvalidMessage.textContent = "Subscription failed. Please try again.";
          this.showNewsletterMessage(this.newsletterInvalidMessage);
      }
  }

  openInEditor(){
    const sidebar = document.querySelector("#sidebar");
    const isSidebarOpen = sidebar && sidebar.classList.contains("open");
    const cartDrawer = document.querySelector("cart-drawer");
    if(isSidebarOpen){
      closeSidebar();
    }
    cartDrawer.close();

    this.classList.add("open");
    this.removeAttribute("inert");
    // document.body.classList.add("overflow-hidden");
    // document.body.classList.add("newsletter-overlay");
    // setTimeout(() => {
    //     trapFocus(this,this.newsletterInput );
    // }, 110);
  }

  closeInEditor(){
    this.classList.remove("open");
    this.setAttribute("inert", "");
    // document.body.classList.remove("overflow-hidden");
    // document.body.classList.remvoe("newsletter-overlay");
  }

  handleSubmit(event) {
      event.preventDefault();
      if (!this.validateNewsletterEmail()) return;
      this.subscribeToNewsletter(this.newsletterInput.value.trim());
  }
}

customElements.define("newsletter-popup", NewsletterPopup);

class emailSignupBanner extends HTMLElement {
  constructor() {
    super();
    this.handleMediaToggle = this.handleMediaToggle.bind(this);
    this.handleAutoPlay = this.handleAutoPlay.bind(this);
  }

  connectedCallback() {
    this.addEventListener("click", this.handleClick);
    this.addEventListener("keydown", this.handleKeyDown);
    this.handleAutoPlay();
  }

  handleClick(event) {
    if (event.target.classList.contains("hp-banner-fw-media-toggle")) {
      this.handleMediaToggle(event);
    } else if (event.target.closest(".hp-banner-fw-media-volume")) {
    }
  }

  handleKeyDown(event) {
    if (event.key === "Enter") {
      this.handleClick(event);
    }
  }

  handleMediaToggle(event) {
    let mediaContainer = this.querySelector(".email-signup-banner-media");
    if (!mediaContainer) return;

    let videos = mediaContainer.querySelectorAll("video");
    let video = videos.length > 1 && window.matchMedia("(max-width: 768px)").matches ? videos[1] : videos[0];

    if (!video) return;

    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  }

  handleAutoPlay() {
    let mediaContainer = this.querySelector(".email-signup-banner-media");
    if (!mediaContainer) return;

    let videos = mediaContainer.querySelectorAll("video");
    let video = videos.length > 1 && window.matchMedia("(max-width: 768px)").matches ? videos[1] : videos[0];

    if (!video) return;

    if(mediaContainer.dataset.autoplay === "true") {
      video.play();
    }
  }

}

customElements.define("email-signup-banner", emailSignupBanner);

// Klaviyo form for Email Signup Banner
document.addEventListener("DOMContentLoaded", () => {
  const newsletterForm = document.getElementById("newsletter-email-signup-banner");
  if (!newsletterForm) return;

  const emailInput = document.getElementById("email-signup-banner-newsletter-input");
  const invalidMessage = document.querySelector(".email-signup-banner-newsletter-messages .invalid-email");
  const emptyMessage = document.querySelector(".email-signup-banner-newsletter-messages .empty-email");
  const successMessage = document.querySelector(".email-signup-banner-newsletter-messages .success-message");
  const listId = newsletterForm.dataset.klaviyoListId;
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const showMessage = (element) => {
    emptyMessage.classList.add("hidden");
    invalidMessage.classList.add("hidden");
    if (element) element.classList.remove("hidden");
  };

   const hideMessage = (element) => {
    if (element) element.classList.add("hidden");
  };

  setTimeout(() => {
    hideMessage();
  }, 5000);

  const validateEmail = () => {
    const email = emailInput.value.trim();
    if (!email) {
      showMessage(emptyMessage);
      setTimeout(() => {
        hideMessage(emptyMessage);
      }, 5000);
      return false;
    }
    if (!emailPattern.test(email)) {
      showMessage(invalidMessage);
      setTimeout(() => {
        hideMessage(invalidMessage);
      }, 5000);
      return false;
    }
    showMessage(null);
    return true;
  };

  const subscribe = async (email) => {
    try {
      if (!listId) {
        console.error("Klaviyo List ID is missing.");
        return;
      }

      const response = await fetch("https://a.klaviyo.com/client/subscriptions/?company_id=JLBJ7v", {
        method: "POST",
        headers: {
          accept: "application/vnd.api+json",
          revision: "2025-01-15",
          "content-type": "application/vnd.api+json"
        },
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: {
              custom_source: "Footer Form",
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email,
                    subscriptions: {
                      email: {
                        marketing: { consent: "SUBSCRIBED" }
                      }
                    }
                  }
                }
              }
            },
            relationships: {
              list: { data: { type: "list", id: listId } }
            }
          }
        })
      });

      if (response.status === 202) {
        emailInput.value = "";
        // emailInput.placeholder = "You're subscribed!";
        newsletterForm.setAttribute("data-submitted", "true");

        // const successMsg = document.createElement("small");
        // successMsg.className = "input-success typography--body-1";
        // successMsg.textContent = "Thank you for subscribing!";
        // newsletterForm.appendChild(successMsg);
        showMessage(successMessage);
        setTimeout(() => {
          hideMessage(successMessage);
          newsletterForm.setAttribute("data-submitted", "false");
        }, 5000);
        
      } else {
        throw new Error("Failed to subscribe");
      }
    } catch (error) {
      const span = invalidMessage.querySelector("span");
      if (span) span.textContent = "Subscription failed. Please try again.";
      showMessage(invalidMessage);
      setTimeout(() => {
          hideMessage(invalidMessage);
        }, 5000);
    }
  };

  newsletterForm.addEventListener("submit", (event) => {
    event.preventDefault();
    if (validateEmail()) {
      subscribe(emailInput.value);
    }
  });
});


class lookbookGallerySlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    this.initSlider();
    window.addEventListener("resize", this.handleResize);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.handleResize);
    this.destroySlider();
  }

  initSlider() {
    const slidesDesktop = Number(this.dataset.slidesDesktop) || 4;
    const slidesMobile = Number(this.dataset.slidesMobile) || 1;
    const autoplay = this.dataset.autoplay === "true";

    const enableMobileSlider = this.dataset.mobileSlider === "true";

    const wrapper = this.closest(".lookbook-main-conatiner");
    const progressBar = wrapper?.querySelector(".lookbook-progress-bar");

    const isMobile = window.innerWidth < 768;

    if (isMobile && !enableMobileSlider) {
      this.classList.add("slider-disabled");
      this.destroySlider();
      return;
    } else {
      this.classList.remove("slider-disabled");
    }

    if (this.swiper) return;

    const config = {
      slidesPerView: slidesMobile,
      spaceBetween: 6,
      freeMode: false,
      grabCursor: true,

      mousewheel: {
        forceToAxis: true
      },

      navigation: {
        nextEl: wrapper.querySelector(".lookbook-arrow-next"),
        prevEl: wrapper.querySelector(".lookbook-arrow-prev")
      },

      autoplay: autoplay ? {
            delay: 3500,
            disableOnInteraction: false
        } : false,

      breakpoints: {
        0: {
          slidesPerView: slidesMobile,
          pagination: {
            el: wrapper.querySelector(".lookbook-progress-mobile"),
            type: "progressbar"
          }
        },
        769: {
          slidesPerView: slidesDesktop,
          spaceBetween: 8,
          freeMode: true,
          pagination: {
            el: wrapper.querySelector(".lookbook-counter"),
            type: "fraction"
          }
        }
      },
    };

    this.swiper = new Swiper(this, config);

    if (progressBar && this.swiper) {
      const updateProgress = () => {
        const total = this.swiper.slides.length;
        const current = this.swiper.activeIndex + 1;
        const progress = (current / total) * 100;
        progressBar.style.width = progress + "%";
      };

      updateProgress();
      this.swiper.on("slideChange", updateProgress);
    }
  }

  destroySlider() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  handleResize() {
    const isMobile = window.innerWidth < 768;
    const enableMobileSlider = this.dataset.mobileSlider === "true";

    if (isMobile && !enableMobileSlider) {
      this.classList.add("slider-disabled");
      this.destroySlider();
      return;
    }

    this.classList.remove("slider-disabled");
    if (this.swiper) {
      this.swiper.update();
      this.swiper.setBreakpoint();
    } else {
      this.initSlider();
    }
  }
}

customElements.define("lookbook-gallery-slider", lookbookGallerySlider);